'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { uuidv7 } from 'uuidv7';
import { usePageContext } from '../../../../../hooks/PageContext/usePageContext';
import { useSettings } from '../../../../../hooks/useSettings';
import { useFeatureFlag } from '../../../../../hooks/FeatureFlagsProvider';
import { getAppsGatewayUrl } from '../../../../../../../constants/env';
import styles from './Disco.module.css';
import { Layout } from './components/Layout';
import { CoverModal } from './components/CoverModal';
import { CoverTeaser } from './components/CoverTeaser';
import { CoverInline } from './components/CoverInline';
import { CoverLoading } from './components/CoverLoading';
import { EddDrawer } from './components/EddDrawer';
import { EddOfferCard } from './components/EddOfferCard';
import { useEddHeader } from './hooks/useEddHeader';
import { BannerOffer, type BannerOfferContent } from './components/BannerOffer';
import { BannerInlineCard } from './components/BannerInlineCard';
import {
  BANNER_FORMAT_FLAG,
  BANNER_HEADLINE,
  isBannerFormatArm,
  resolveBannerExperience,
  type BannerArrival,
  type BannerNavControl,
} from './bannerFormat';
import SlideableDrawerBanner from '../../../../ui/SlideableDrawer/SlideableDrawerBanner';
import Panel from '../../../../ui/Panel/Panel';
import PanelHeader from '../../../../ui/PanelHeader/PanelHeader';
import {
  CUSTOM_BRANDING_FLAG,
  CUSTOM_BRANDING_GRADIENT_START,
  DiscoLayout,
  EDD_DRAWER_FLAG,
  VIEWS,
  isCustomBrandingArm,
  isEddDrawerArm,
} from './constants';
import { Analytics } from './analytics';
import { useViewability } from './hooks/useViewability';
import {
  fetchDiscoRecommendations,
  sha256Hex16,
  type DiscoServerData,
} from './fetchRecommendations';
import type { AdProperties, AnalyticsConfig } from './types';
import type { VendorInitiatingTarget } from './schemas';
import { resolvePageUrl } from '../../willSkipAnalytics';
import {
  readVisitRecord,
  visitForPageLoad,
  writeVisitRecord,
} from './visitHistory';
import {
  getBlockedOpenDiagnostics,
  watchOpenedWindow,
  watchRedirectDeparture,
} from './blockedOpenDiagnostics';

interface DiscoProps {
  jwt: string;
  layout: DiscoLayout;
  isCoverImage: boolean;
  fixedPanelHeight?: string;
  view?: (typeof VIEWS)[keyof typeof VIEWS];
  decisionId?: string;
  isFallback?: boolean;
  initiallyDismissed?: boolean;
  onDrawerOpen?: () => void;
  visitNumber?: number;
  visitId?: string;
}

type CoverPhase = 'hidden' | 'teaser' | 'carousel' | 'inline';

const TEASER_OFFER_COUNT = 4;

const useMountEffect = (fn: React.EffectCallback) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fn, []);

function Disco({
  jwt,
  layout,
  isCoverImage,
  fixedPanelHeight,
  view = VIEWS.ORDER_TRACKING,
  decisionId,
  isFallback,
  initiallyDismissed = false,
  onDrawerOpen,
  visitNumber,
  visitId,
}: DiscoProps) {
  const {
    state: {
      retailerMoniker,
      deviceType: ctxDeviceType,
      versionName,
      requestUrl,
      localeLanguage,
      localeCountry,
      trackingStatus,
      orderNumbers,
      trackingNumbers,
      carrier,
      sessionId: ctxSessionId,
    },
  } = usePageContext();
  const settings = useSettings();
  const customBrandingArm = useFeatureFlag(CUSTOM_BRANDING_FLAG);
  const bannerArm = useFeatureFlag(BANNER_FORMAT_FLAG);
  const bannerEligible = layout === DiscoLayout.Drawer && !isCoverImage;
  const bannerTagged = bannerEligible && isBannerFormatArm(bannerArm);
  const visitOrderKey = orderNumbers?.[0] || trackingNumbers?.[0];
  const [storedVisit] = useState(() =>
    bannerTagged && visitOrderKey && visitId
      ? visitForPageLoad(readVisitRecord(visitOrderKey), visitId, Date.now())
      : 1,
  );
  const visit = visitNumber ?? storedVisit;
  const bannerExperience = bannerEligible
    ? resolveBannerExperience(bannerArm, visit)
    : null;
  const bannerExperienceKind = bannerExperience?.kind;
  const bannerDismissedInline =
    bannerExperienceKind === 'drawer' && initiallyDismissed;
  const bannerNavigation = bannerDismissedInline
    ? undefined
    : bannerExperience?.kind === 'drawer'
      ? bannerExperience.navigation
      : bannerExperience?.kind === 'inline'
        ? 'arrows'
        : undefined;
  const showsBanner = bannerTagged && bannerExperienceKind !== 'control';
  const eddDrawerArm = useFeatureFlag(EDD_DRAWER_FLAG);
  const eddHeader = useEddHeader();
  const eddDrawerTagged =
    bannerEligible &&
    !bannerTagged &&
    !!eddHeader &&
    isEddDrawerArm(eddDrawerArm);
  const arrivalRef = useRef<BannerArrival>('initial');
  const offerShownAtRef = useRef<number | null>(null);
  const msOnPreviousOfferRef = useRef<number | null>(null);
  const [failedImages, setFailedImages] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const deviceType = ctxDeviceType ?? 'desktop';
  const isMobile = deviceType === 'mobile';
  const customBrandingTagged =
    isMobile && isCoverImage && isCustomBrandingArm(customBrandingArm);
  const pageUrl = resolvePageUrl(requestUrl);
  const locale =
    localeLanguage && localeCountry
      ? `${localeLanguage}_${localeCountry}`
      : 'en_US';

  const [sessionId] = useState(() => ctxSessionId ?? uuidv7());
  const [externalId, setExternalId] = useState('');
  const [serverData, setServerData] = useState<DiscoServerData | null>(null);
  const [recommendationIndex, setRecommendationIndex] = useState(0);
  const [coverPhase, setCoverPhase] = useState<CoverPhase>('hidden');
  const [isDismissed, setIsDismissed] = useState(initiallyDismissed);
  const [mobileClaimed, setMobileClaimed] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const fetchedRef = useRef(false);

  useMountEffect(() => {
    const identifier = orderNumbers?.[0] || trackingNumbers?.[0] || '';
    if (!identifier) return;
    void sha256Hex16(identifier).then(setExternalId);
  });

  const triggerFetch = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    const orderNumber = orderNumbers?.[0];
    const trackingNumber = trackingNumbers?.[0];
    if (!orderNumber && !trackingNumber) return;
    fetchDiscoRecommendations({
      jwt,
      sessionId,
      retailerMoniker,
      orderNumber,
      trackingNumber: orderNumber ? undefined : trackingNumber,
      carrierMoniker: carrier,
      trackingStatus,
      deviceType,
      layout,
      isCoverImage,
      bannerFormatArm: bannerTagged ? bannerArm : undefined,
      eddDrawerArm: eddDrawerTagged ? eddDrawerArm : undefined,
      sandbox: settings.retailMedia?.sandbox ?? false,
      locale,
      view,
    }).then(setServerData);
  }, [
    jwt,
    sessionId,
    retailerMoniker,
    orderNumbers,
    trackingNumbers,
    carrier,
    trackingStatus,
    deviceType,
    layout,
    isCoverImage,
    bannerTagged,
    bannerArm,
    eddDrawerTagged,
    eddDrawerArm,
    settings.retailMedia?.sandbox,
    locale,
    view,
  ]);

  useMountEffect(() => {
    if (!bannerTagged || !visitOrderKey || !visitId) return;
    writeVisitRecord(visitOrderKey, {
      visit: storedVisit,
      lastVisitId: visitId,
      lastSeenAt: Date.now(),
    });
  });

  useMountEffect(() => {
    if (!isCoverImage) {
      triggerFetch();
    }
  });

  useMountEffect(() => {
    if (!isCoverImage || isMobile) return;
    const timer = setTimeout(
      () => setCoverPhase((prev) => (prev === 'hidden' ? 'teaser' : prev)),
      1000,
    );
    return () => clearTimeout(timer);
  });

  const browserFingerprintRef = useRef('');
  const fingerprintInitRef = useRef(false);
  if (!fingerprintInitRef.current && typeof window !== 'undefined') {
    fingerprintInitRef.current = true;
    import('@narvar/userprint').then(({ getUserprint }) =>
      getUserprint().then((result: { browser_fingerprint: string }) => {
        browserFingerprintRef.current = result.browser_fingerprint;
      }),
    );
  }

  const analyticsConfig = useMemo<AnalyticsConfig>(
    () => ({
      gatewayUrl: getAppsGatewayUrl(),
      jwt,
      sessionId,
      vendorSessionId: serverData?.vendorSessionId ?? '',
      retailerMoniker,
      device: deviceType,
      pageUrl,
      layout,
      view,
      versionName,
      locale,
      get browserFingerprint() {
        return browserFingerprintRef.current;
      },
      orderNumber: serverData?.orderNumber ?? orderNumbers?.[0],
      trackingNumber: serverData?.trackingNumber ?? trackingNumbers?.[0],
      trackingStatus,
      numAdsReturned: serverData?.numAdsReturned ?? 0,
      externalId,
      decisionId,
      isFallback,
      experiments:
        customBrandingTagged || bannerTagged || eddDrawerTagged
          ? {
              ...(customBrandingTagged && {
                [CUSTOM_BRANDING_FLAG]: customBrandingArm,
              }),
              ...(bannerTagged && { [BANNER_FORMAT_FLAG]: bannerArm }),
              ...(eddDrawerTagged && { [EDD_DRAWER_FLAG]: eddDrawerArm }),
            }
          : undefined,
      payloadAttributes: bannerTagged
        ? {
            visitNumber: visit,
            bannerExperience: bannerDismissedInline
              ? 'dismissed_inline'
              : bannerExperienceKind,
            ...(bannerNavigation && { bannerNavigation }),
          }
        : undefined,
    }),
    [
      sessionId,
      externalId,
      serverData,
      orderNumbers,
      trackingNumbers,
      jwt,
      retailerMoniker,
      deviceType,
      pageUrl,
      layout,
      view,
      versionName,
      locale,
      trackingStatus,
      decisionId,
      isFallback,
      customBrandingTagged,
      customBrandingArm,
      bannerTagged,
      bannerArm,
      eddDrawerTagged,
      eddDrawerArm,
      visit,
      bannerExperienceKind,
      bannerDismissedInline,
      bannerNavigation,
    ],
  );

  const data = useMemo(() => {
    if (!serverData?.group?.length) return null;
    return {
      recommendations: serverData.recommendations,
      group: serverData.group,
      footer: serverData.footer,
      analyticsConfig,
    };
  }, [serverData, analyticsConfig]);

  const adProperties = useMemo<AdProperties | undefined>(() => {
    if (!data?.group.length || !data?.recommendations) return undefined;
    const current = data.group[recommendationIndex];
    if (!current) return undefined;
    const recommendation = data.recommendations[current.offer_id];
    if (!recommendation) return undefined;
    const featuredImageUrl =
      (recommendation.offer.metadata.layout_type === 'FEATURED_IMAGE' ||
        recommendation.offer.metadata.layout_type === 'BANNER_VISUAL') &&
      recommendation.offer.assets.length > 0
        ? recommendation.offer.assets[0].image_url
        : undefined;
    return {
      offerId: current.offer_id,
      advertiserId: current.advertiser_id,
      advertiserName: recommendation.advertiser.name,
      insertionId: current.insertion_id,
      position: recommendationIndex,
      offerContent: {
        heading: recommendation.offer.heading,
        subheading: recommendation.offer.subheading,
        targetUrl: recommendation.offer.target_url,
        acceptCopy: recommendation.offer.cta_details.accept_copy,
        declineCopy: recommendation.offer.cta_details.decline_copy,
        featuredImageUrl,
        layoutType: recommendation.offer.metadata.layout_type,
        vendorAdvertiserLogoUrl: recommendation.advertiser.logo_url,
      },
    };
  }, [data, recommendationIndex]);

  const currentRecommendation = useMemo(() => {
    if (!data?.recommendations || !adProperties?.offerId) return null;
    return data.recommendations[adProperties.offerId];
  }, [data?.recommendations, adProperties]);

  const hasMoreRecommendations = data
    ? recommendationIndex < data.group.length
    : false;

  const analyticsRef = useRef<Analytics | null>(null);
  if (!analyticsRef.current && externalId && data) {
    analyticsRef.current = new Analytics(analyticsConfig);
  }

  useEffect(() => {
    analyticsRef.current?.trackWidgetLoad();
  }, [externalId, data]);

  const handleWidgetDisplay = useCallback(
    () => analyticsRef.current?.trackWidgetDisplay(),
    [],
  );
  const handleWidgetView = useCallback(
    () => analyticsRef.current?.trackWidgetView(),
    [],
  );
  const bannerFallbacks = useCallback(
    (ad: AdProperties): string[] => {
      const imageUrl = ad.offerContent?.featuredImageUrl;
      return [
        ...(bannerExperienceKind === 'inline' &&
        (!imageUrl || failedImages.has(imageUrl))
          ? ['image_fill']
          : []),
        ...(!ad.offerContent?.vendorAdvertiserLogoUrl ? ['logo_missing'] : []),
      ];
    },
    [failedImages, bannerExperienceKind],
  );

  const handleBannerImageError = (imageUrl: string) =>
    setFailedImages((failed) => new Set(failed).add(imageUrl));

  const handleAdDisplay = useCallback(() => {
    if (!adProperties) return;
    if (offerShownAtRef.current === null) {
      offerShownAtRef.current = performance.now();
    }
    if (!bannerTagged) {
      analyticsRef.current?.trackAdDisplay(adProperties);
      return;
    }
    analyticsRef.current?.trackAdDisplay(adProperties, {
      arrivedVia: arrivalRef.current,
      ...(msOnPreviousOfferRef.current !== null && {
        msOnPreviousOffer: msOnPreviousOfferRef.current,
      }),
      ...(showsBanner && { fallbacks: bannerFallbacks(adProperties) }),
    });
  }, [adProperties, bannerTagged, showsBanner, bannerFallbacks]);
  const handleAdView = useCallback(() => {
    if (!adProperties) return;
    analyticsRef.current?.trackAdView(
      adProperties,
      showsBanner ? { fallbacks: bannerFallbacks(adProperties) } : undefined,
    );
  }, [adProperties, showsBanner, bannerFallbacks]);

  const widgetRef = useViewability(handleWidgetDisplay, handleWidgetView);
  const adRef = useViewability(handleAdDisplay, handleAdView, {
    resetKey: adProperties?.offerId,
  });

  const trackAdClick = useCallback(
    (initiatingTarget: 'INLINE_ACTION_BUTTON' | 'INLINE_ASSET_TILE') => {
      if (!adProperties) return;
      analyticsRef.current?.trackAdClick(adProperties, initiatingTarget);
    },
    [adProperties],
  );

  const trackOfferTabBlocked = useCallback(
    (
      initiatingTarget: 'INLINE_ACTION_BUTTON' | 'INLINE_ASSET_TILE',
      blockReason?: string,
    ) => {
      if (!adProperties) return;
      const diagnostics = getBlockedOpenDiagnostics();
      analyticsRef.current?.trackOfferTabBlocked(
        adProperties,
        initiatingTarget,
        blockReason ? { ...diagnostics, blockReason } : diagnostics,
      );
    },
    [adProperties],
  );

  const trackElementClick = useCallback(
    (
      initiatingTarget: VendorInitiatingTarget,
      payloadAttributes?: Record<string, unknown>,
    ) => {
      if (
        initiatingTarget === 'INLINE_NAV_FORWARD' ||
        initiatingTarget === 'INLINE_NAV_BACKWARD'
      ) {
        analyticsRef.current?.trackElementClick(
          initiatingTarget,
          adProperties,
          payloadAttributes,
        );
      } else {
        analyticsRef.current?.trackElementClick(initiatingTarget);
      }
    },
    [adProperties],
  );

  useEffect(() => {
    if (
      isCoverImage &&
      !isMobile &&
      coverPhase === 'carousel' &&
      data &&
      !hasMoreRecommendations
    ) {
      setCoverPhase('inline');
    }
  }, [isCoverImage, isMobile, coverPhase, data, hasMoreRecommendations]);

  const handleAccept = () => {
    if (!currentRecommendation || !adProperties?.offerId) return;
    trackAdClick('INLINE_ACTION_BUTTON');
    const opened = window.open(
      currentRecommendation.offer.target_url,
      '_blank',
    );
    if (!opened) {
      trackOfferTabBlocked('INLINE_ACTION_BUTTON');
      watchRedirectDeparture(() =>
        trackOfferTabBlocked('INLINE_ACTION_BUTTON', 'redirect_failed'),
      );
      window.location.assign(currentRecommendation.offer.target_url);
      return;
    }
    watchOpenedWindow(opened, () =>
      trackOfferTabBlocked('INLINE_ACTION_BUTTON', 'opened_then_closed'),
    );
    markArrival('claim');
    setRecommendationIndex(recommendationIndex + 1);
  };

  const handleDecline = () => {
    const msOnOffer = markArrival('decline');
    trackElementClick(
      'INLINE_NAV_FORWARD',
      bannerTagged
        ? {
            navControl: 'decline',
            fromIndex: recommendationIndex,
            toIndex: recommendationIndex + 1,
            ...(msOnOffer !== null && { msOnOffer }),
          }
        : undefined,
    );
    setRecommendationIndex(recommendationIndex + 1);
  };

  const handleAssetClick = (targetUrl: string) => {
    trackAdClick('INLINE_ASSET_TILE');
    const opened = window.open(targetUrl, '_blank');
    if (!opened) {
      trackOfferTabBlocked('INLINE_ASSET_TILE');
      watchRedirectDeparture(() =>
        trackOfferTabBlocked('INLINE_ASSET_TILE', 'redirect_failed'),
      );
      window.location.assign(targetUrl);
      return;
    }
    watchOpenedWindow(opened, () =>
      trackOfferTabBlocked('INLINE_ASSET_TILE', 'opened_then_closed'),
    );
    markArrival('claim');
    setRecommendationIndex(recommendationIndex + 1);
  };

  const handleDismiss = (source: 'button' | 'overlay' | 'swipe') => {
    const target = source === 'overlay' ? 'CLOSE_BACKDROP' : 'CLOSE_BUTTON';
    trackElementClick(target);
    setIsDismissed(true);
  };

  function markArrival(via: BannerArrival): number | null {
    const now = performance.now();
    const msOnOffer =
      offerShownAtRef.current === null
        ? null
        : Math.round(now - offerShownAtRef.current);
    arrivalRef.current = via;
    msOnPreviousOfferRef.current = msOnOffer;
    offerShownAtRef.current = now;
    return msOnOffer;
  }

  const handleBannerNav = (
    direction: 'prev' | 'next',
    control: BannerNavControl,
  ) => {
    const total = data?.group.length ?? 0;
    const next =
      direction === 'next'
        ? Math.min(recommendationIndex + 1, total - 1)
        : Math.max(recommendationIndex - 1, 0);
    if (next === recommendationIndex) return;
    const msOnOffer = markArrival(control);
    trackElementClick(
      direction === 'next' ? 'INLINE_NAV_FORWARD' : 'INLINE_NAV_BACKWARD',
      {
        navControl: control,
        fromIndex: recommendationIndex,
        toIndex: next,
        ...(msOnOffer !== null && { msOnOffer }),
      },
    );
    setRecommendationIndex(next);
  };

  const handleBannerAutoAdvance = () => {
    const total = data?.group.length ?? 0;
    if (recommendationIndex >= total - 1) return;
    markArrival('auto');
    setRecommendationIndex(recommendationIndex + 1);
  };

  const handleBannerDrawerOpen = () => {
    setDrawerOpened(true);
    onDrawerOpen?.();
  };

  const handleCoverDismiss = (source: 'button' | 'backdrop') => {
    const target = source === 'backdrop' ? 'CLOSE_BACKDROP' : 'CLOSE_BUTTON';
    trackElementClick(target);
    setCoverPhase('inline');
  };

  const handleClaimOffersDesktop = () => {
    triggerFetch();
    setCoverPhase('carousel');
  };

  const handleClaimOffersMobile = () => {
    triggerFetch();
    setMobileClaimed(true);
  };

  const layoutProps = useMemo(() => {
    if (!currentRecommendation || !adProperties) return null;
    const { offer, advertiser } = currentRecommendation;
    const hasAssetImage =
      offer.metadata.layout_type === 'FEATURED_IMAGE' &&
      offer.assets.length > 0 &&
      offer.assets[0].asset_type === 'IMAGE';
    const imageUrl = hasAssetImage
      ? offer.assets[0].image_url
      : advertiser.logo_url;
    const showImage = Boolean(imageUrl);

    return {
      heading: offer.heading,
      subheading: offer.subheading,
      disclosureDetails: offer.disclosure_details,
      jsonContent: offer.json_content,
      showImage,
      imageUrl: showImage ? imageUrl : undefined,
      imageAlt: hasAssetImage ? offer.heading : advertiser.name,
      imageTargetUrl: hasAssetImage
        ? offer.assets[0].target_url || undefined
        : undefined,
      offerId: adProperties.offerId,
      acceptCopy: offer.cta_details.accept_copy,
      declineCopy: offer.cta_details.decline_copy,
      onAccept: handleAccept,
      onDecline: handleDecline,
      onAssetClick: handleAssetClick,
      currentIndex: recommendationIndex + 1,
      totalCount: data?.group.length || 0,
      widgetRef,
      adRef,
      advertiserLogoUrl: currentRecommendation.advertiser.logo_url,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentRecommendation,
    adProperties,
    recommendationIndex,
    data?.group.length,
    widgetRef,
    adRef,
  ]);

  if (isCoverImage && !isMobile) {
    const showTeaser = coverPhase === 'teaser';
    const showLoading = coverPhase === 'carousel' && !serverData;
    const showCarousel =
      coverPhase === 'carousel' && layoutProps && hasMoreRecommendations;

    const modal = showTeaser ? (
      <CoverModal
        onClose={() => handleCoverDismiss('button')}
        onBackdropClick={() => handleCoverDismiss('backdrop')}
      >
        <CoverTeaser
          offerCount={TEASER_OFFER_COUNT}
          retailerMoniker={retailerMoniker}
          onClaimOffers={handleClaimOffersDesktop}
        />
      </CoverModal>
    ) : showLoading ? (
      <CoverModal
        onClose={() => handleCoverDismiss('button')}
        onBackdropClick={() => handleCoverDismiss('backdrop')}
      >
        <CoverLoading />
      </CoverModal>
    ) : showCarousel ? (
      <CoverModal
        onClose={() => handleCoverDismiss('button')}
        onBackdropClick={() => handleCoverDismiss('backdrop')}
        noPadding
      >
        <Layout variant="cover" {...layoutProps} />
      </CoverModal>
    ) : null;

    const showInline = !serverData || hasMoreRecommendations;

    if (!modal && !showInline) {
      return null;
    }

    return (
      <>
        {modal}
        {showInline && (
          <CoverInline
            offerCount={data?.group.length ?? TEASER_OFFER_COUNT}
            onInvoke={handleClaimOffersDesktop}
          />
        )}
      </>
    );
  }

  if (isCoverImage && isMobile) {
    if (isDismissed && (hasMoreRecommendations || !mobileClaimed)) {
      return (
        <CoverInline
          offerCount={data?.group.length ?? TEASER_OFFER_COUNT}
          onInvoke={() => {
            setIsDismissed(false);
            handleClaimOffersMobile();
          }}
        />
      );
    }

    if (mobileClaimed && serverData && !hasMoreRecommendations) {
      return null;
    }

    const delaySeconds = mobileClaimed ? 0 : 1;

    return (
      <SlideableDrawerBanner
        delaySeconds={delaySeconds}
        onDismiss={handleDismiss}
        onOpen={onDrawerOpen}
        hideBannerTitle
      >
        {mobileClaimed && layoutProps ? (
          <div className={styles.mobileCoverWrapper}>
            <Layout
              variant="cover"
              {...layoutProps}
              onDismiss={() => handleDismiss('button')}
              coverGradientStart={
                customBrandingArm === 'INDIGO'
                  ? CUSTOM_BRANDING_GRADIENT_START
                  : undefined
              }
            />
          </div>
        ) : mobileClaimed ? (
          <div className={styles.mobileCoverWrapper}>
            <CoverLoading />
          </div>
        ) : (
          <div
            className={styles.mobileCoverWrapper}
            style={{ padding: '2rem' }}
          >
            <CoverTeaser
              offerCount={TEASER_OFFER_COUNT}
              retailerMoniker={retailerMoniker}
              onClaimOffers={handleClaimOffersMobile}
            />
          </div>
        )}
      </SlideableDrawerBanner>
    );
  }

  if (!hasMoreRecommendations) return null;
  if (!layoutProps) return null;

  if (bannerExperience && bannerExperience.kind !== 'control' && data) {
    const bannerOffer: BannerOfferContent | null =
      currentRecommendation && adProperties
        ? {
            offerId: adProperties.offerId,
            heading: currentRecommendation.offer.heading,
            subheading: currentRecommendation.offer.subheading,
            disclosureDetails: currentRecommendation.offer.disclosure_details,
            jsonContent: currentRecommendation.offer.json_content,
            advertiserName: currentRecommendation.advertiser.name,
            logoUrl: currentRecommendation.advertiser.logo_url || undefined,
            heroImageUrl: adProperties.offerContent?.featuredImageUrl,
            acceptCopy: currentRecommendation.offer.cta_details.accept_copy,
            declineCopy: currentRecommendation.offer.cta_details.decline_copy,
          }
        : null;

    if (!bannerOffer) return null;

    const total = data.group.length;
    const bannerProps = {
      offer: bannerOffer,
      index: recommendationIndex,
      total,
      onPrev: (control: BannerNavControl) => handleBannerNav('prev', control),
      onNext: (control: BannerNavControl) => handleBannerNav('next', control),
      onAccept: handleAccept,
      onDecline: handleDecline,
      widgetRef,
      adRef,
    };

    if (bannerExperience.kind === 'drawer' && !isDismissed) {
      return (
        <SlideableDrawerBanner
          title={BANNER_HEADLINE}
          delaySeconds={1}
          onDismiss={handleDismiss}
          onOpen={handleBannerDrawerOpen}
        >
          <BannerOffer
            {...bannerProps}
            navigation={bannerExperience.navigation}
            autoAdvance={drawerOpened}
            onAutoAdvance={handleBannerAutoAdvance}
          />
        </SlideableDrawerBanner>
      );
    }

    if (bannerExperience.kind === 'inline') {
      return (
        <Panel
          header={null}
          fixedPanelHeight={fixedPanelHeight}
          sInner={{ padding: 0 }}
        >
          <BannerInlineCard
            {...bannerProps}
            onImageError={handleBannerImageError}
          />
        </Panel>
      );
    }
  }

  const header = <PanelHeader>You&apos;ve Unlocked This Gift</PanelHeader>;

  if (layout === DiscoLayout.Drawer && !isDismissed) {
    const delaySeconds = 1;
    const drawer = (
      <SlideableDrawerBanner
        title="You've Unlocked This Gift"
        delaySeconds={delaySeconds}
        onDismiss={handleDismiss}
        onOpen={onDrawerOpen}
      >
        <Layout variant="drawer" {...layoutProps} />
      </SlideableDrawerBanner>
    );

    if (eddDrawerTagged && eddHeader && eddDrawerArm === 'EDD') {
      return (
        <EddDrawer
          header={eddHeader}
          delaySeconds={delaySeconds}
          onDismiss={handleDismiss}
          onOpen={onDrawerOpen}
        >
          <EddOfferCard {...layoutProps} />
        </EddDrawer>
      );
    }

    return drawer;
  }

  return (
    <Panel
      header={header}
      fixedPanelHeight={fixedPanelHeight}
      sOuter={{ minHeight: '150px' }}
      sInner={{ padding: '1.5rem' }}
    >
      <Layout variant="inline" {...layoutProps} />
    </Panel>
  );
}

export default Disco;
